// @ts-check
// Seção do caso de retenção (contrato 10 de atendimento, seção 24.5): o desfecho com Em acompanhamento, Ficou e Saiu (o
// motivo e as matrículas que saem), a cadência de contatos e o aviso da Lei 9.870/1999. Usada pela tela Casos de
// retenção (ao abrir um caso) e pronta para a seção do atendimento do tipo RETENTION (Notas da F5).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { fetchRetentionCase, recordRetentionOutcome } from '../../data/retention.js';
import { OUTCOME_COLORS, findOutcomeProblem } from '../../lib/retention-display.js';
import { formatDateTime } from '../../lib/format.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { showServiceError } from '../service-desk/service-errors.js';
import { hasTranslation, t } from '../../i18n/index.js';

/** @typedef {import('../../data/retention.js').RetentionCaseDetail} RetentionCaseDetail */

const OUTCOMES = /** @type {const} */ (['MONITORING', 'STAYED', 'LEFT']);
const FALLBACK_EXIT_REASONS = ['FINANCIAL', 'RELOCATION', 'DISSATISFACTION', 'ACADEMIC_PERFORMANCE', 'HEALTH', 'TRANSFER_TO_COMPETITOR', 'SCHEDULE', 'COMPLETED_STUDIES', 'OTHER'];

/** Motivos de saída pelos metadados da matrícula (a escola renomeia), ou os do pacote. */
function exitReasonOptions() {
  const field = objectsByNameSingular.value.get('enrollment')?.fields.find((candidate) => candidate.name === 'exitReason');
  const options = /** @type {Array<{ value: string, label: string, position?: number }>} */ (field?.options ?? []);
  if (options.length > 0) return [...options].sort((left, right) => (left.position ?? 0) - (right.position ?? 0)).map((option) => ({ value: option.value, label: option.label }));
  return FALLBACK_EXIT_REASONS.map((value) => ({ value, label: t(`retention.exitReasons.${value}`) }));
}

/** @param {string | null} status */
const reEnrollmentLabel = (status) => (hasTranslation(`retention.reEnrollmentStatuses.${status ?? 'NONE'}`) ? t(`retention.reEnrollmentStatuses.${status ?? 'NONE'}`) : String(status));

/** Valor do campo datetime-local (hora local) a partir de um instante. @param {string | null} iso */
function toLocalInput(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** @param {{ ticketId: string, onChanged?: () => void }} props */
export function RetentionCaseSection({ ticketId, onChanged }) {
  const [detail, setDetail] = useState(/** @type {RetentionCaseDetail | null | 'hidden'} */ (null));
  const [outcome, setOutcome] = useState(/** @type {string | null} */ (null));
  const [exitReason, setExitReason] = useState('');
  const [enrollmentIds, setEnrollmentIds] = useState(/** @type {string[]} */ ([]));
  const [nextContact, setNextContact] = useState('');
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showProblem, setShowProblem] = useState(false);
  const load = () => {
    fetchRetentionCase(ticketId).then((loaded) => {
      setDetail(loaded);
      setEnrollmentIds(loaded.enrollments.map((enrollment) => enrollment.id));
      setNextContact(toLocalInput(loaded.nextContactAt));
    }).catch((error) => {
      if (!(error?.statusCode === 403 || error?.statusCode === 404)) console.error(error);
      setDetail('hidden');
    });
  };
  useEffect(load, [ticketId]);
  if (detail === 'hidden') return null;
  if (!detail) return html`<div class="retention-case" aria-busy="true"><${Skeleton} width="100%" height=${80} /></div>`;
  const nextContactIso = nextContact ? new Date(nextContact).toISOString() : null;
  const problem = findOutcomeProblem({ outcome, exitReason, enrollmentIds, nextContactAt: nextContactIso, hasEnrollments: detail.enrollments.length > 0 });
  const save = async () => {
    if (problem) {
      setShowProblem(true);
      return;
    }
    setIsSaving(true);
    try {
      await recordRetentionOutcome(ticketId, {
        outcome: /** @type {'MONITORING' | 'STAYED' | 'LEFT'} */ (outcome), note: note.trim(),
        ...(outcome === 'LEFT' ? { exitReason, enrollmentIds } : {}), ...(outcome === 'MONITORING' ? { nextContactAt: nextContactIso } : {}),
      });
      showSuccessSnackbar(t('retention.case.saved'));
      setOutcome(null);
      setNote('');
      setShowProblem(false);
      load();
      onChanged?.();
    } catch (error) {
      showServiceError(error);
    } finally {
      setIsSaving(false);
    }
  };
  const toggleEnrollment = (/** @type {string} */ id) => setEnrollmentIds(enrollmentIds.includes(id) ? enrollmentIds.filter((candidate) => candidate !== id) : [...enrollmentIds, id]);

  return html`<div class="retention-case" data-testid="retention-case-section">
    ${detail.transferNotice ? html`<p class="retention-law-notice" role="note"><${Icon} name="IconInfoCircle" size=${14} />${t('retention.lawNotice')}</p>` : null}
    ${detail.cadence.length > 0
      ? html`<div class="retention-case-block">
          <h3 class="retention-subtitle">${t('retention.case.cadenceTitle')}</h3>
          <ol class="retention-cadence">
            ${detail.cadence.map((entry, index) => html`<li key=${entry.taskId ?? index} data-done=${entry.isDone ? 'true' : 'false'}>
              <${Icon} name=${entry.isDone ? 'IconCircleCheck' : 'IconCircle'} size=${14} />
              <span>${t('retention.case.contact', { index: index + 1, date: formatDateTime(entry.dueAt) })}</span>
              <span class="retention-muted">${entry.isDone ? t('retention.case.done') : t('retention.case.pending')}</span>
            </li>`)}
          </ol>
        </div>`
      : null}
    ${detail.isOpen
      ? html`<div class="retention-case-block">
          <h3 class="retention-subtitle">${t('retention.case.outcomeTitle')}</h3>
          <div class="retention-outcomes" role="radiogroup" aria-label=${t('retention.case.outcomeTitle')}>
            ${OUTCOMES.map((value) => html`<label key=${value} class="retention-outcome-option" data-selected=${outcome === value ? 'true' : 'false'}>
              <input type="radio" name=${`retention-outcome-${ticketId}`} value=${value} checked=${outcome === value} onChange=${() => setOutcome(value)} />
              <span>${t(`retention.outcomes.${value}`)}</span>
            </label>`)}
          </div>
          ${outcome ? html`<p class="retention-muted">${t(`retention.case.outcomeHints.${outcome}`)}</p>` : null}
          ${outcome === 'LEFT'
            ? html`<label class="retention-dialog-field">
                <span class="retention-field-label">${t('retention.case.exitReason')}</span>
                <select class="retention-select" value=${exitReason} onChange=${(/** @type {any} */ event) => setExitReason(event.currentTarget.value)}>
                  <option value="">${t('retention.case.chooseExitReason')}</option>
                  ${exitReasonOptions().map((option) => html`<option key=${option.value} value=${option.value}>${option.label}</option>`)}
                </select>
              </label>
              <fieldset class="retention-fieldset">
                <legend class="retention-field-label">${t('retention.case.enrollmentsTitle')}</legend>
                ${detail.enrollments.length === 0 ? html`<p class="retention-muted">${t('retention.case.noEnrollments')}</p>` : null}
                ${detail.enrollments.map((enrollment) => html`<label key=${enrollment.id} class="retention-check">
                  <input type="checkbox" checked=${enrollmentIds.includes(enrollment.id)} onChange=${() => toggleEnrollment(enrollment.id)} />
                  <span>${enrollment.name ?? enrollment.id}</span>
                  ${enrollment.previousStatus !== null || enrollment.reEnrollmentStatus === 'AT_RISK'
                    ? html`<span class="retention-muted">${t('retention.case.previousStatus', { status: reEnrollmentLabel(enrollment.previousStatus) })}</span>` : null}
                </label>`)}
              </fieldset>`
            : null}
          ${outcome === 'MONITORING'
            ? html`<label class="retention-dialog-field">
                <span class="retention-field-label">${t('retention.case.nextContactAt')}</span>
                <input type="datetime-local" class="retention-input" value=${nextContact} onInput=${(/** @type {any} */ event) => setNextContact(event.currentTarget.value)} />
              </label>`
            : null}
          ${outcome
            ? html`<label class="retention-dialog-field">
                <span class="retention-field-label">${t('retention.case.note')}</span>
                <textarea class="retention-textarea" rows="3" maxlength="4000" value=${note} placeholder=${t('retention.case.notePlaceholder')}
                  onInput=${(/** @type {any} */ event) => setNote(event.currentTarget.value)}></textarea>
              </label>`
            : null}
          ${showProblem && problem ? html`<p class="retention-problem" role="alert">${t(`retention.case.problems.${problem}`)}</p>` : null}
          <div class="retention-section-actions">
            <${Button} title=${t('retention.case.save')} accent="blue" size="small" disabled=${!outcome} isLoading=${isSaving} onClick=${save} />
          </div>
        </div>`
      : html`<p class="retention-case-closed">
          <${Tag} color=${OUTCOME_COLORS[detail.outcome ?? ''] ?? 'gray'} text=${t('retention.case.closed', { outcome: t(`retention.outcomes.${detail.outcome ?? 'NONE'}`) })} />
        </p>`}
  </div>`;
}
